import React, { useState, useEffect, useRef, useContext } from 'react'

import { accessibilityLabel } from '@components/kit/_support'
import ScrollView from '@components/kit/scroll-view'

import Content from '@components/content'
import { useNutritionStore } from '@store/Nutrition'
import {
  NutritionDailySummary,
  NutritionDayStatus,
  NutritionMonthlySummary
} from '@store/Nutrition/types'
import NutritionCalendar, { DateStatus } from './nutrition-calendar'
import DateNavigation from './date-navigation'
import DailyGoals from './daily-goals'
import Meals from './meals'
import { ChatContext } from '../chat.web'

const transformMonthlySummaryToStatus = (
  summary: NutritionMonthlySummary | null
): DateStatus => {
  if (!summary) return {}

  const status: DateStatus = {}
  summary.forEach(item => {
    status[item.day] =
      item.meals.length > 0 ? [item.status] : [NutritionDayStatus.NO_MEAL]
  })

  return status
}

function Nutrition({ ...props }) {
  const { userId } = useContext(ChatContext)
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  const [dateStatus, setDateStatus] = useState<DateStatus>({})
  const [monthlySummary, setMonthlySummary] =
    useState<NutritionMonthlySummary | null>(null)
  const [dailyNutrition, setDailyNutrition] =
    useState<NutritionDailySummary | null>(null)
  const lastFetchedMonth = useRef<string | null>(null)
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date())

  const { getNutritionMonthlySummary, getNutritionDailySummary } =
    useNutritionStore.getState()

  const handleMonthChange = (date: Date) => {
    setCurrentMonth(date)
  }

  useEffect(() => {
    const fetchMonthlySummary = async () => {
      const month = String(currentMonth.getMonth() + 1)
      const year = currentMonth.getFullYear()
      const currentMonthKey = `${year}-${month}`

      if (
        lastFetchedMonth.current !== currentMonthKey ||
        monthlySummary === null
      ) {
        const summary = await getNutritionMonthlySummary(userId, month, year)
        setMonthlySummary(summary)
        setDateStatus(transformMonthlySummaryToStatus(summary))
        lastFetchedMonth.current = currentMonthKey
      }
    }

    fetchMonthlySummary()
  }, [currentMonth, getNutritionMonthlySummary, monthlySummary, userId])

  useEffect(() => {
    const fetchDailySummary = async () => {
      const year = selectedDate.getFullYear()
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0')
      const day = String(selectedDate.getDate()).padStart(2, '0')
      const dateString = `${year}${month}${day}`

      const daily = await getNutritionDailySummary(userId, dateString)
      setDailyNutrition(daily)
    }

    fetchDailySummary()
  }, [selectedDate, getNutritionDailySummary, userId])

  return (
    <ScrollView {...accessibilityLabel('Nutrition')} {...props}>
      <Content gap='16'>
        <NutritionCalendar
          dateStatus={dateStatus}
          date={selectedDate}
          onDateSelect={setSelectedDate}
          onMonthChange={handleMonthChange}
        />
        <DateNavigation
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
        />
        <DailyGoals
          calories={{
            goal: dailyNutrition?.goal?.daily_calories || 0,
            value: dailyNutrition?.nutrition.calories || 0
          }}
          carbs={{
            goal: dailyNutrition?.goal?.daily_carbs || 0,
            value: dailyNutrition?.nutrition.carbs || 0
          }}
          protein={{
            goal: dailyNutrition?.goal?.daily_protein || 0,
            value: dailyNutrition?.nutrition.protein || 0
          }}
          fat={{
            goal: dailyNutrition?.goal?.daily_fat || 0,
            value: dailyNutrition?.nutrition.fat || 0
          }}
        />
        <Meals meals={dailyNutrition?.meals || []} />
      </Content>
    </ScrollView>
  )
}

export default Nutrition
